import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { HelpCircle, ChevronDown } from "lucide-react";

/**
 * AI-generated FAQ block at the bottom of an article.
 * Also injects FAQPage JSON-LD into <head> for Google rich results.
 */
export default function FAQBox({ articleId }) {
  const [faq, setFaq] = useState([]);
  const [open, setOpen] = useState(0);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const { data } = await api.post(`/news/${articleId}/faq`);
        if (alive) setFaq(Array.isArray(data?.faq) ? data.faq : []);
      } catch { if (alive) setFaq([]); }
      finally { if (alive) setLoading(false); }
    })();
    return () => { alive = false; };
  }, [articleId]);

  // Inject FAQPage JSON-LD (Google rich result) when we have entries.
  useEffect(() => {
    if (!faq.length) return;
    const ld = {
      "@context": "https://schema.org",
      "@type": "FAQPage",
      mainEntity: faq.map((f) => ({
        "@type": "Question",
        name: f.q,
        acceptedAnswer: { "@type": "Answer", text: f.a },
      })),
    };
    const el = document.createElement("script");
    el.type = "application/ld+json";
    el.id = "article-faq-jsonld";
    el.text = JSON.stringify(ld);
    document.head.appendChild(el);
    return () => { document.getElementById("article-faq-jsonld")?.remove(); };
  }, [faq]);

  if (loading || !faq.length) return null;

  return (
    <section className="mt-10 titans-card p-6 md:p-8 border-2 border-titans-blue/25" data-testid="faq-box">
      <div className="flex items-center gap-2 mb-5">
        <HelpCircle size={18} className="text-titans-blue" />
        <h3 className="font-display text-2xl md:text-3xl uppercase tracking-wide">Frequently Asked</h3>
      </div>
      <div className="divide-y divide-[rgba(15,23,42,0.08)]">
        {faq.map((f, i) => (
          <details
            key={i}
            open={i === open}
            onToggle={(e) => { if (e.currentTarget.open) setOpen(i); }}
            data-testid={`faq-item-${i}`}
            className="group py-3"
          >
            <summary className="cursor-pointer flex items-start gap-3 list-none">
              <span className="text-titans-red font-display text-xl leading-none pt-0.5">{String(i + 1).padStart(2, "0")}</span>
              <span className="font-display uppercase text-lg leading-tight flex-1">{f.q}</span>
              <ChevronDown size={16} className="mt-1 shrink-0 text-titans-blue transition-transform group-open:rotate-180" />
            </summary>
            <div className="pl-9 pt-2 pr-6 font-body text-[15px] leading-relaxed">{f.a}</div>
          </details>
        ))}
      </div>
    </section>
  );
}
